import { Target, Heart, Zap, Calendar } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import pieterImage from "@assets/ph_1749794413664.webp";
import lillyImage from "@assets/Lilly_1749794413663.webp";
import vickyImage from "@assets/vicky_1749794413665.webp";

export default function About() {

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark-slate to-charcoal text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyber-purple rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-drift"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-cyber-blue rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-orbit" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-neon-pink rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-spiral" style={{animationDelay: '4s'}}></div>
      </div>

      <Navigation onBookCall={() => {}} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-8">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              About <span className="text-cyber-purple text-shadow">Us</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              We deliver pre-vetted South African developers to your team in 7 days or less. No recruitment fees. Flexible contracts. Just results.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-4 text-lg text-gray-300 leading-relaxed">
              <p>
                Our mission is simple: to make hiring top South African tech talent seamless, fast, and reliable.
              </p>
              <p>
                We believe South Africa is home to some of the best developers, engineers, and architects in the world — professionals who bring skill, work ethic, and time zone alignment to companies in the USA and Europe.
              </p>
              <p>
                We understand how hard it is to scale tech teams quickly without sacrificing quality. That's why we've built a system that delivers high-performing South African experts who are ready to make an impact from day one.
              </p>
              <p>
                Whether you're a startup hiring your first engineer or a scaleup needing specialised firepower — we'll match you with the right expert, right when you need them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Leaders Section */}
      <section className="py-8 bg-gradient-to-r from-charcoal to-dark-slate">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Meet Our <span className="text-cyber-purple">Leaders</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="relative w-32 h-32 mx-auto mb-6">
                <img 
                  src={pieterImage} 
                  alt="Pieter Human - Founder & CEO"
                  className="w-full h-full rounded-full object-cover border-4 border-cyber-purple group-hover:border-neon-pink transition-all duration-300"
                />
                <div className="absolute inset-0 rounded-full bg-cyber-gradient opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-2xl font-bold mb-2">Pieter Human</h3>
              <h4 className="text-cyber-purple font-semibold mb-4">Founder & CEO</h4>
              <p className="text-gray-300">A visionary leader with a strong background in tech entrepreneurship and business growth. Pieter ensures UnicornClub stays ahead in the competitive hiring landscape.</p>
            </div>

            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="relative w-32 h-32 mx-auto mb-6">
                <img 
                  src={lillyImage} 
                  alt="Annelie van Zyl - COO"
                  className="w-full h-full rounded-full object-cover border-4 border-neon-pink group-hover:border-cyber-purple transition-all duration-300"
                />
                <div className="absolute inset-0 rounded-full bg-cyber-gradient opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-2xl font-bold mb-2">Annelie van Zyl</h3>
              <h4 className="text-neon-pink font-semibold mb-4">COO</h4>
              <p className="text-gray-300">A seasoned management consultant, Annelie oversees the OPS teams, ensuring the best quality of delivery for our clients.</p>
            </div>

            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="relative w-32 h-32 mx-auto mb-6">
                <img 
                  src={vickyImage} 
                  alt="Vicky Steyn - Tech Team Builder"
                  className="w-full h-full rounded-full object-cover border-4 border-cyber-gold group-hover:border-electric-blue transition-all duration-300"
                />
                <div className="absolute inset-0 rounded-full bg-cyber-gradient opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-2xl font-bold mb-2">Vicky Steyn</h3>
              <h4 className="text-cyber-gold font-semibold mb-4">Tech Team Builder</h4>
              <p className="text-gray-300">With years of experience in global tech recruitment, Vicky connects businesses with highly skilled IT experts tailored to their needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-6">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="glass-card p-6 md:p-8 rounded-2xl">
              <h3 className="text-3xl font-bold mb-6 text-cyber-purple">The Story Behind UnicornClub</h3>
              
              <div className="space-y-4 text-lg text-gray-300 leading-relaxed">
                <p>
                  Founded by Pieter Human, a seasoned data and solutions architect with over two decades of experience, UnicornClub was born out of a simple but powerful observation: South Africa has world-class tech talent that's underutilized globally.
                </p>
                
                <p>
                  Back in 2013, I founded Vito Solutions, a company focused on delivering high-quality IT consulting and contract resources to South African corporates. We built a solid reputation and worked with some of the biggest names locally – but we were mostly operating within our borders.
                </p>
                
                <p>
                  Over time, I noticed a growing need in the USA and Europe for highly skilled developers, engineers, and architects – especially in fast-moving sectors like fintech and SaaS. At the same time, top South African experts were looking for more exciting work, better pay, and global exposure.
                </p>
                
                <p>
                  That's when UnicornClub was born.
                </p>
                
                <p>
                  UnicornClub connects top-tier South African IT experts with funded startups and growing companies in the USA and Europe, giving our clients access to affordable, reliable talent – without compromising on quality. We believe in remote work done right. We focus on cultural fit, time zone alignment, and getting business outcomes, not just filling roles.
                </p>
                
                <p>
                  I built UnicornClub for founders, CTOs, and tech leads who need to scale fast with a lean budget and without the hassle of offshore outsourcing nightmares.
                </p>
                
                <p className="text-xl font-semibold text-cyber-gold">
                  If that sounds like you, welcome to the club.
                </p>
                
                <div className="pt-8 border-t border-gray-700 mt-8">
                  <p className="text-xl font-bold text-white">Pieter Human</p>
                  <p className="text-cyber-purple font-semibold">Founder, UnicornClub</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-8 bg-gradient-to-r from-charcoal to-dark-slate">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our Core <span className="text-cyber-purple">Principles</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Target className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Transparency & Trust</h3>
              <p className="text-gray-300">We believe in clear, honest communication and building lasting relationships based on mutual trust and reliability.</p>
            </div>

            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Heart className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Long-term Relationships</h3>
              <p className="text-gray-300">We focus on creating lasting partnerships that benefit both our clients and our talented developers for years to come.</p>
            </div>

            <div className="glassmorphism rounded-2xl p-8 text-center group hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-cyber-gradient rounded-full flex items-center justify-center mx-auto mb-6 group-hover:animate-pulse">
                <Zap className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">Scalability & Flexibility</h3>
              <p className="text-gray-300">Our solutions adapt to your growing needs, providing the right talent at the right time as your business evolves.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 bg-gradient-to-r from-cyber-purple/20 to-neon-pink/20">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Meet Your <span className="text-cyber-gold">Next Team Member</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join hundreds of successful companies who've found their perfect developers through UnicornClub.
            </p>
            <Link href="/project-form">
              <Button 
                className="bg-cyber-gradient hover:bg-gradient-to-r hover:from-neon-pink hover:to-cyber-purple text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 animate-glow"
              >
                <Calendar className="mr-3" />
                Start Hiring Today
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}